import React, { useContext } from 'react'
import Image from 'next/image'
import { Box, Typography, useTheme } from '@mui/material'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'

import ProfileContext from '@/components/Context/ProfileContext'
import { getDomainSetting } from '@/lib/getDomainSettings'

type Service = {
  key: string
  name: string
  description: string
  href: string
  logo?: string
  Icon?: React.ComponentType<{ sx?: object }>
  disabled?: boolean
}

const extranetGroupUrl = getDomainSetting('urlExtranetGroup') || ''

const SERVICES: readonly Service[] = [
  {
    key: 'sound',
    name: 'PharmediSound',
    description: 'Communication sonore en officine',
    href: `${extranetGroupUrl}frontoffice/sound?entity=PHARMEDISOUND`,
    logo: '/images/services/pmsound.png',
  },
  {
    key: 'school',
    name: 'PharmediSchool',
    description: 'Formations pour équipes officinales',
    href: `${extranetGroupUrl}frontoffice/school?entity=PHARMEDISCHOOL`,
    logo: '/images/services/pmschool.png',
  },
  {
    key: 'sacherie',
    name: 'Sacherie',
    description: 'Sacs et emballages pour pharmacies',
    href: '#',
    Icon: ShoppingBagIcon,
    disabled: true, // TODO(COPIL 2026-03-31) — reactiver quand le service Sacherie sera pret
  },
] as const

const AeliaServicesBanner: React.FC = () => {
  const { profile } = useContext(ProfileContext)
  const theme = useTheme()

  if (!profile?.groupAeliaStore) return null

  return (
    <Box
      sx={{
        mt: 6,
        mb: 4,
        py: 4,
        px: { xs: 2, md: 4 },
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: { xs: '18px', md: '22px' },
          textTransform: 'uppercase',
          mb: 3,
        }}
      >
        Découvrez aussi nos services
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: { xs: 2, md: 4 },
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        {SERVICES.filter((s) => !s.disabled).map(({ key, name, description, href, logo, Icon }) => (
          <Box
            component="a"
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              width: { xs: '100%', md: 520 },
              display: 'flex',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'box-shadow 0.2s, transform 0.2s',
                height: '100%',
                width: '100%',
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Box
                sx={{
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                {logo ? (
                  <Image
                    src={logo}
                    alt={name}
                    width={180}
                    height={72}
                    style={{ objectFit: 'contain', maxHeight: 72, width: 'auto' }}
                  />
                ) : Icon ? (
                  <Icon sx={{ fontSize: 56, color: theme.palette.primary.main }} />
                ) : null}
              </Box>
              <Typography sx={{ fontWeight: 'bold', fontSize: '18px', mb: 0.5 }}>
                {name}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default AeliaServicesBanner
